import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useAuthState } from "react-firebase-hooks/auth";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

import { auth, db } from "../../components/firebase";
import { doc, onSnapshot } from "firebase/firestore";

import PortfolioPanel from "../components/ProfileComp/PortfolioPanel";
import SettingsPanel from "../components/ProfileComp/SettingsPanel";
import LeaderboardPanel from "../components/ProfileComp/LeaderboardPanel";
import NotificationsPanel from "../components/ProfileComp/NotificationsPanel";
import SupportPanel from "../components/ProfileComp/SupportPanel";
import ReferralPanel from "../components/ProfileComp/ReferralPanel";

const R12 = "12px";
const GLASS = {
  panel: "linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)",
  surface: "linear-gradient(180deg, rgba(12,16,22,.92) 0%, rgba(8,11,17,.92) 60%)",
  border: "1px solid rgba(255,255,255,.08)",
  borderStrong: "1px solid rgba(255,255,255,.16)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const Shell = styled(Box)({ minHeight: "100vh", background: "#020617" });

const LEFT_W = 236;

const Sidebar = styled(Card)(({ theme }) => ({
  position: "sticky",
  top: theme.spacing(2),
  borderRadius: R12,
  padding: 10,
  background: GLASS.panel,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
}));

const NavItem = styled(Button)(({ active }) => ({
  justifyContent: "flex-start",
  textTransform: "none",
  fontWeight: 850,
  borderRadius: 10,
  padding: "8px 10px",
  minHeight: 40,
  marginBottom: 8,
  color: GLASS.text,
  border: active ? GLASS.borderStrong : GLASS.border,
  background: active ? "rgba(255,255,255,.07)" : "rgba(255,255,255,.04)",
  boxShadow: active ? "0 6px 18px rgba(2,8,23,.30)" : "none",
  "& .MuiButton-startIcon": { marginRight: 10 },
  "&:hover": {
    background: "rgba(255,255,255,.08)",
    transform: "translateY(-1px)",
    boxShadow: "0 8px 22px rgba(2,8,23,.40)",
  },
}));

const HeaderCard = styled(Card)({
  borderRadius: 18,
  background: GLASS.panel,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
});

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(String(text || ""));
  } catch {}
}

function pickMainIdentifier(userDoc, fallbackUid) {
  const ap = userDoc?.authProviders || {};
  if (ap?.email === true && userDoc?.email) return { kind: "email", value: userDoc.email };
  if (ap?.wallet === true) {
    const w = userDoc?.primaryWallet || userDoc?.wallets?.[0]?.address;
    if (w) return { kind: "wallet", value: w };
  }
  return { kind: "uid", value: userDoc?.uid || fallbackUid || "—" };
}

function getInitialTabKey() {
  try {
    const p = new URLSearchParams(window.location.search);
    const tab = String(p.get("tab") || "").trim().toLowerCase();
    const allowed = new Set(["portfolio", "settings", "leaderboard", "notifications", "support", "referrals"]);
    if (allowed.has(tab)) return tab;
  } catch {}
  return "portfolio";
}

export default function ProfilePage() {
  const [user] = useAuthState(auth);

  const [userDoc, setUserDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);

  const menu = useMemo(
    () => [
      { key: "portfolio", label: "Portfolio", icon: <BarChartIcon /> },
      { key: "settings", label: "Settings", icon: <SettingsIcon /> },
      { key: "referrals", label: "Referrals", icon: <GroupAddIcon /> },
      { key: "leaderboard", label: "Leaderboard", icon: <EmojiEventsIcon /> },
      { key: "notifications", label: "Notifications", icon: <NotificationsIcon /> },
      { key: "support", label: "Support", icon: <SupportAgentIcon /> },
    ],
    []
  );

  const [activeKey, setActiveKey] = useState(getInitialTabKey);

  useEffect(() => {
    if (!user?.uid) return;

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setUserDoc(snap.exists() ? snap.data() : null);
        setLoadingDoc(false);
      },
      () => setLoadingDoc(false)
    );

    return () => unsub();
  }, [user?.uid]);

  const mainId = useMemo(() => pickMainIdentifier(userDoc, user?.uid), [userDoc, user?.uid]);

  if (!user) {
    return (
      <Shell sx={{ p: 3 }}>
        <Typography sx={{ color: GLASS.text }}>Not authenticated</Typography>
      </Shell>
    );
  }

  const RightContent = () => {
    if (activeKey === "portfolio") return <PortfolioPanel />;
    if (activeKey === "settings") return <SettingsPanel />;
    if (activeKey === "referrals") return <ReferralPanel userDoc={userDoc} />;
    if (activeKey === "leaderboard") return <LeaderboardPanel />;
    if (activeKey === "notifications") return <NotificationsPanel />;
    if (activeKey === "support") return <SupportPanel />;
    return <PortfolioPanel />;
  };

  const nickname = String(userDoc?.nickname || "").trim();
  const avatarUrl = userDoc?.avatarUrl || auth.currentUser?.photoURL || "";
  const verified = Boolean(userDoc?.verified);

  return (
    <Shell>
      <Box sx={{ maxWidth: 1560, mx: "auto", px: { xs: 1, md: 2 }, py: 3 }}>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <HeaderCard sx={{ p: { xs: 1.25, sm: 1.75 }, mb: 2 }}>
            {loadingDoc ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <CircularProgress size={18} />
                <Typography sx={{ color: GLASS.subtext, fontWeight: 800 }}>Loading profile…</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
                  <Avatar
                    src={avatarUrl}
                    sx={{
                      width: 54,
                      height: 54,
                      border: "1px solid rgba(255,255,255,.14)",
                      bgcolor: "rgba(255,255,255,.06)",
                      flex: "0 0 auto",
                    }}
                  />

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontWeight: 1000,
                          fontSize: 18,
                          color: GLASS.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: "100%", md: 520 },
                        }}
                        title={nickname || "No nickname"}
                      >
                        {nickname || "No nickname"}
                      </Typography>

                      <Chip
                        size="small"
                        label={verified ? "verified" : "not verified"}
                        sx={{
                          height: 22,
                          borderRadius: 1,
                          fontWeight: 900,
                          bgcolor: verified ? "rgba(20,241,149,.14)" : "rgba(248,113,113,.14)",
                          color: GLASS.text,
                          border: "1px solid rgba(255,255,255,.12)",
                          "& .MuiChip-label": { px: 0.75, fontSize: 11, lineHeight: "22px", fontWeight: 900 },
                        }}
                      />
                    </Box>

                    <Typography sx={{ mt: 0.9, fontWeight: 950, fontSize: 12, color: GLASS.subtext }}>
                      Main identifier
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.35, minWidth: 0, flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontWeight: 950,
                          fontSize: 14,
                          color: GLASS.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: "100%", md: 520 },
                        }}
                        title={mainId.value}
                      >
                        {mainId.kind === "wallet" ? shortAddr(mainId.value) : mainId.value}
                      </Typography>

                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={() => copyToClipboard(mainId.value)} sx={{ color: GLASS.text }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Chip
                        size="small"
                        label={mainId.kind}
                        sx={{
                          height: 22,
                          borderRadius: 1,
                          fontWeight: 900,
                          bgcolor: "rgba(56,189,248,.10)",
                          color: GLASS.text,
                          border: "1px solid rgba(255,255,255,.12)",
                          "& .MuiChip-label": { px: 0.75, fontSize: 11, lineHeight: "22px", fontWeight: 900 },
                        }}
                      />
                    </Box>

                    <Typography sx={{ mt: 0.6, color: GLASS.subtext, fontSize: 12 }}>
                      UID:{" "}
                      <Box
                        component="span"
                        sx={{
                          color: GLASS.text,
                          fontWeight: 900,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {userDoc?.uid || user.uid}
                      </Box>
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    size="small"
                    label={`role: ${String(userDoc?.role || "client")}`}
                    sx={{ color: GLASS.text, border: "1px solid rgba(255,255,255,.12)", bgcolor: "rgba(255,255,255,.04)", fontWeight: 900 }}
                  />
                  <Chip
                    size="small"
                    label={`plan: ${String(userDoc?.plan || "free")}`}
                    sx={{ color: GLASS.text, border: "1px solid rgba(255,255,255,.12)", bgcolor: "rgba(255,255,255,.04)", fontWeight: 900 }}
                  />
                </Box>
              </Box>
            )}
          </HeaderCard>
        </motion.div>

        <Grid container spacing={2}>
          <Grid item xs={12} md="auto" sx={{ width: { md: LEFT_W } }}>
            <Sidebar sx={{ width: { md: LEFT_W } }}>
              <Typography variant="subtitle2" sx={{ color: GLASS.subtext, mb: 1, fontWeight: 900 }}>
                Menu
              </Typography>

              {menu.map((it) => (
                <NavItem
                  key={it.key}
                  fullWidth
                  active={activeKey === it.key ? 1 : 0}
                  startIcon={it.icon}
                  onClick={() => setActiveKey(it.key)}
                >
                  {it.label}
                </NavItem>
              ))}
            </Sidebar>
          </Grid>

          <Grid item xs={12} md sx={{ minWidth: 0 }}>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <RightContent />
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Shell>
  );
}
